import { KeyRound, Mail, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { can } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import { StatusPill } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows, errorMessage } from '@/shared/components/data'
import { Button } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { CompanyContact } from '@/shared/types/domain'

import { useCompanyContacts, useGrantPortalAccess } from '../api'
import { ContactForm } from './ContactForm'

export function CompanyContactsTab() {
  const t = useT()
  const { companyId = '' } = useParams()
  const query = useCompanyContacts(companyId)
  const { me } = useSession()
  const [creating, setCreating] = useState(false)
  /** El contacto que se está editando, o `null` si nadie. */
  const [editando, setEditando] = useState<CompanyContact | null>(null)
  const grant = useGrantPortalAccess(companyId)

  const puedeEscribir = can(me, 'contact:write', { companyId })

  const darAcceso = (contactId: string, nombre: string) => {
    grant.mutate(contactId, {
      onSuccess: () => toast.success(t('companies.portal.granted', { persona: nombre })),
      onError: (error) => toast.error(errorMessage(error, t('companies.error.grantAccess'))),
    })
  }

  const alta = puedeEscribir ? (
    <div className="flex justify-end">
      <Button variant="primary" onClick={() => setCreating(true)}>
        {t('companies.contact.new')}
      </Button>
    </div>
  ) : null

  if (query.isPending) return <SkeletonRows rows={4} columns={[0.3, 0.3, 0.2]} withSpine={false} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const contacts = query.data ?? []

  return (
    <div className="flex flex-col gap-3">
      {alta}
      <ContactForm companyId={companyId} open={creating} onClose={() => setCreating(false)} />
      {/*
        `key` con el id: sin él, React reusaría el formulario entre contactos y
        los campos se quedarían con los datos del anterior — se inicializan una
        sola vez, al montar.
      */}
      {editando ? (
        <ContactForm
          key={editando.id}
          companyId={companyId}
          contact={editando}
          open
          onClose={() => setEditando(null)}
        />
      ) : null}

      {contacts.length === 0 ? (
        <EmptyState
          title={t('companies.contacts.emptyTitle')}
          description={t('companies.contacts.emptyDescription')}
        />
      ) : (
        <ul className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1">
          {contacts.map((contact) => (
            <li
              key={contact.id}
              className="hairline-b flex flex-wrap items-center gap-2 px-3 py-2 last:border-b-0"
            >
              <span className="text-xs text-text-1">{contact.name}</span>
              {contact.is_primary ? (
                <StatusPill tone="in_development">{t('companies.contacts.primary')}</StatusPill>
              ) : null}
              {contact.has_login ? (
                <StatusPill tone="neutral">{t('companies.contacts.hasLogin')}</StatusPill>
              ) : null}
              <span className="text-2xs text-text-3">
                {contact.role_title ?? t('companies.contacts.noRole')}
              </span>
              <span className="flex-1" />
              <a
                href={`mailto:${contact.email}`}
                className="num inline-flex items-center gap-1 text-2xs text-text-2 hover:text-signal"
              >
                <Mail size={12} aria-hidden="true" />
                {contact.email}
              </a>
              {/*
                Dar acceso es una acción por contacto y sólo aparece si todavía
                no lo tiene: crea una cuenta que ve datos de la empresa, así que
                no es una casilla del alta — es un clic aparte, deliberado.
              */}
              {puedeEscribir ? (
                <Button onClick={() => setEditando(contact)} title={t('common.action.edit')}>
                  <Pencil size={12} aria-hidden="true" />
                  <span className="sr-only">{t('common.action.edit')}</span>
                </Button>
              ) : null}
              {puedeEscribir && !contact.has_login ? (
                <Button
                  onClick={() => darAcceso(contact.id, contact.name)}
                  disabled={grant.isPending}
                  title={t('companies.portal.hint')}
                >
                  <KeyRound size={12} aria-hidden="true" />
                  {grant.isPending ? t('companies.portal.granting') : t('companies.portal.grant')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
